import { MFA_GRACE_ENDS_AT } from "../../../constants/mfaFlags";
import MfaFactor, {
  MfaEnrollMethod,
  MfaFactorStatus,
  MfaFactorTypeRequest,
} from "../../models/mfaFactor.model";

const MFA_SOFT_PROMPT_DISMISSED_KEY = "mfaSoftPromptDismissed";

const OKTA_VERIFY_FACTOR_TYPES = new Set([
  "token:software:totp",
  "totp",
  "push",
  "okta_verify",
]);

export function enrollMethodToFactorType(
  method: MfaEnrollMethod
): MfaFactorTypeRequest {
  return method === "okta_verify" ? "okta_verify" : "email";
}

export function isOktaVerifyFactorType(factorType: string): boolean {
  return OKTA_VERIFY_FACTOR_TYPES.has(String(factorType).toLowerCase()) ||
    OKTA_VERIFY_FACTOR_TYPES.has(factorType);
}

const MFA_STATUS_PRIORITY: MfaFactorStatus[] = [
  "ACTIVE",
  "ENROLLED",
  "PENDING_ACTIVATION",
  "EXPIRED",
  "INACTIVE",
  "DISABLED",
];

const PROTECTED_MFA_STATUSES = new Set<MfaFactorStatus>(["ACTIVE", "ENROLLED"]);

export function normalizeMfaStatus(
  status: string | undefined
): MfaFactorStatus | null {
  const value = String(status ?? "").toUpperCase();
  return MFA_STATUS_PRIORITY.includes(value as MfaFactorStatus) ||
    value === "NOT_SETUP"
    ? (value as MfaFactorStatus)
    : null;
}

function isSupportedMfaFactor(factor: MfaFactor): boolean {
  return (
    String(factor.factorType).toLowerCase() === "email" ||
    isOktaVerifyFactorType(factor.factorType)
  );
}

function findSupportedFactorByStatus(
  factors: MfaFactor[] | undefined,
  status: MfaFactorStatus
): MfaFactor | undefined {
  if (!factors?.length) return undefined;
  return factors.find(
    (factor) =>
      isSupportedMfaFactor(factor) &&
      normalizeMfaStatus(factor.status) === status
  );
}

/** Best status across supported factors. Empty or unknown lists are NOT_SETUP. */
export function getMfaEnrollmentStatus(
  factors: MfaFactor[] | undefined
): MfaFactorStatus {
  for (const status of MFA_STATUS_PRIORITY) {
    if (findSupportedFactorByStatus(factors, status)) return status;
  }
  return "NOT_SETUP";
}

export function isMfaEnrolled(factors: MfaFactor[] | undefined): boolean {
  const status = getMfaEnrollmentStatus(factors);
  return PROTECTED_MFA_STATUSES.has(status);
}

/** @deprecated Use isMfaEnrolled — kept for call-site clarity during migration */
export function isEmailMfaEnrolled(factors: MfaFactor[] | undefined): boolean {
  return isMfaEnrolled(factors);
}

export function getActiveMfaFactor(
  factors: MfaFactor[] | undefined
): MfaFactor | undefined {
  return (
    findSupportedFactorByStatus(factors, "ACTIVE") ??
    findSupportedFactorByStatus(factors, "ENROLLED")
  );
}

function isProtectedMfaStatus(status: string | undefined): boolean {
  const normalized = normalizeMfaStatus(status);
  return normalized !== null && PROTECTED_MFA_STATUSES.has(normalized);
}

function isEmailFactor(factor: MfaFactor): boolean {
  return String(factor.factorType).toLowerCase() === "email";
}

function findProtectedFactor(
  factors: MfaFactor[] | undefined,
  matches: (factor: MfaFactor) => boolean
): MfaFactor | undefined {
  return factors?.find(
    (factor) => matches(factor) && isProtectedMfaStatus(factor.status)
  );
}

export function getEnrolledEmailFactor(
  factors: MfaFactor[] | undefined
): MfaFactor | undefined {
  return findProtectedFactor(factors, isEmailFactor);
}

export function getEnrolledAuthenticatorFactor(
  factors: MfaFactor[] | undefined
): MfaFactor | undefined {
  return findProtectedFactor(factors, (factor) =>
    isOktaVerifyFactorType(factor.factorType)
  );
}

export function getActiveEmailFactor(
  factors: MfaFactor[] | undefined
): MfaFactor | undefined {
  return getEnrolledEmailFactor(factors);
}

export function getEnrolledMfaMethodLabels(
  factors: MfaFactor[] | undefined
): string[] {
  const labels: string[] = [];
  const email = getEnrolledEmailFactor(factors);
  if (email) {
    labels.push(`Email (${email.profile?.email || "enabled"})`);
  }
  if (getEnrolledAuthenticatorFactor(factors)) {
    labels.push("Authenticator app (Okta Verify)");
  }
  return labels;
}

/** The method still available to add. Null when none or both are enrolled. */
export function getMissingMfaMethod(
  factors: MfaFactor[] | undefined
): MfaEnrollMethod | null {
  const hasEmail = !!getEnrolledEmailFactor(factors);
  const hasAuthenticator = !!getEnrolledAuthenticatorFactor(factors);
  if (hasEmail && !hasAuthenticator) return "okta_verify";
  if (hasAuthenticator && !hasEmail) return "email";
  return null;
}

/** Enrollment started but the one-time code has not been verified yet. */
export function getPendingActivationFactor(
  factors: MfaFactor[] | undefined
): MfaFactor | undefined {
  if (!factors?.length) return undefined;
  return factors.find((factor) => {
    if (String(factor.status).toUpperCase() !== "PENDING_ACTIVATION") {
      return false;
    }
    return isSupportedMfaFactor(factor);
  });
}

export function getMaskedEmailFromFactors(
  factors: MfaFactor[] | undefined
): string | undefined {
  const activeEmail = getActiveEmailFactor(factors);
  if (activeEmail?.profile?.email) return activeEmail.profile.email;
  const pending = factors?.find((f) => f.factorType === "email");
  return pending?.profile?.email;
}

export function getMfaMethodLabel(factors: MfaFactor[] | undefined): string {
  const enrolled = getEnrolledMfaMethodLabels(factors);
  if (enrolled.length > 0) return enrolled.join(" and ");
  const active = getActiveMfaFactor(factors);
  if (!active) return "";
  if (isEmailFactor(active)) {
    return `Email (${active.profile?.email || "enabled"})`;
  }
  if (isOktaVerifyFactorType(active.factorType)) {
    return "Authenticator app (Okta Verify)";
  }
  return "Enabled";
}

export function getMfaStatusDescription(
  factors: MfaFactor[] | undefined
): string {
  const status = getMfaEnrollmentStatus(factors);
  const enrolled = getEnrolledMfaMethodLabels(factors);
  switch (status) {
    case "ACTIVE":
    case "ENROLLED":
      if (enrolled.length === 0) return "Enabled — two-step verification.";
      return enrolled.map((label) => `Enabled — ${label}.`).join(" ");
    case "PENDING_ACTIVATION":
      return "Setup started. Finish verification to turn on two-step verification.";
    case "INACTIVE":
      return "Two-step verification is turned off.";
    case "DISABLED":
      return "Two-step verification is disabled.";
    case "EXPIRED":
      return "Two-step verification expired. Set it up again.";
    default:
      return "Choose email or an authenticator app to protect your account when signing in.";
  }
}

export function getTotpQrHref(factor: MfaFactor | undefined): string | undefined {
  if (!factor) return undefined;
  const activation = factor._embedded?.activation ?? factor.activation;
  return (
    activation?.qrCode ||
    activation?.qrcode?.href ||
    activation?.qrCodeUrl ||
    activation?.qrCodeHref
  );
}

export function getTotpSharedSecret(
  factor: MfaFactor | undefined
): string | undefined {
  if (!factor) return undefined;
  const activation = factor._embedded?.activation ?? factor.activation;
  return activation?.sharedSecret;
}

export function isMfaGracePeriodActive(now: Date = new Date()): boolean {
  if (!MFA_GRACE_ENDS_AT) return true;
  const endsAt = new Date(MFA_GRACE_ENDS_AT);
  if (Number.isNaN(endsAt.getTime())) return true;
  return now.getTime() < endsAt.getTime();
}

function graceEndCalendarDay(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return Date.UTC(year, month - 1, day);
}

export function getMfaDaysRemaining(now: Date = new Date()): number {
  if (!MFA_GRACE_ENDS_AT) return 14;
  const endsAt = new Date(MFA_GRACE_ENDS_AT);
  const endDay = graceEndCalendarDay(MFA_GRACE_ENDS_AT);
  if (endDay === null || Number.isNaN(endsAt.getTime())) return 14;
  if (now.getTime() >= endsAt.getTime()) return 0;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((endDay - today) / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 1;
}

export function isMfaSoftPromptDismissed(): boolean {
  try {
    return sessionStorage.getItem(MFA_SOFT_PROMPT_DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}

export function dismissMfaSoftPrompt(): void {
  try {
    sessionStorage.setItem(MFA_SOFT_PROMPT_DISMISSED_KEY, "true");
  } catch {
    // Ignore storage failures (private mode, etc.)
  }
}

/** Next login should show the setup popup again. */
export function clearMfaSoftPromptDismissal(): void {
  try {
    sessionStorage.removeItem(MFA_SOFT_PROMPT_DISMISSED_KEY);
  } catch {
    // Ignore storage failures (private mode, etc.)
  }
}
